import React, {useCallback, useMemo, useState} from 'react';
import {Grid, Tooltip, Card, CardContent, Box, Chip} from '@mui/material';
import {Done} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
//import { getDistanceFromLatLonInKm } from '@/utils';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '@/redux-store/store';
import {CardMediaComponent} from '@/components/ui/card-media';
import LockIcon from '@mui/icons-material/Lock';
import {env} from '@/config';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import {GroupData} from '@/types/group';
import {Sport} from '@/types/common';
import {getDistanceFromLatLonInKm} from '@/utils';

const {CONTENT_SERVER} = env;

interface GroupCardBigProps {
  group: GroupData;
  onGroupCardClick: (group: GroupData) => void;
  fullWidth?: boolean;
  cardMediaHeight?: string;
  cardMediaTypography?: 'h4' | 'h6';
  customTitleWidth?: string;
  alwaysTruncate?: boolean;
  smallerVariant?: boolean;
}

const groupsImage = `${CONTENT_SERVER}assets/sports/team-new.jpg`;

const getImageSource = (group: GroupData, sport: Sport | null): string => {
  if (group.picture) {
    return `${group.picture}?timestamp=${group.updatedAt}`;
  }
  if (group.sports && group.sports.length > 1) {
    return groupsImage;
  }
  return sport?.picture || groupsImage;
};

const GroupCardBig: React.FC<GroupCardBigProps> = ({
  group,
  onGroupCardClick,
  fullWidth = false,
  cardMediaHeight,
  cardMediaTypography,
  smallerVariant = false
}) => {
  const {t, i18n} = useTranslation();

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const [showMainTooltip, setShowMainTooltip] = useState(false);

  const userInfos = useAppSelector((state) => state.userInfos.userInfos);

  const {sports} = useAppSelector((state) => state.sports);

  const groupFilter = useAppSelector((state) => state.filter['group']);

  const handleOtherTooltipMouseEnter = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const handleOtherTooltipMouseLeave = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const isUserMember =
    group.members.findIndex((m) => m._id === userInfos._id) >= 0;

  const chipSize = smallerVariant ? 'small' : 'medium';
  const sportId = group.sports[0]?.sport || group.sports[0]?._id;

  const sport = typeof sportId === 'string' ? sports[sportId] : sportId;

  const sportLabel =
    i18n.language === 'en' ? sport?.labels.en : sport?.labels.de;

  const imageSource = getImageSource(group, sport);

  const responsiveTypographyTitleProps = smallerVariant ? {} : {medium: true};

  const groupDistance = useMemo(() => {
    let distance: number;
    const filterCoords = groupFilter.location?.coordinates;
    if (filterCoords && group.location.coordinates) {
      distance = getDistanceFromLatLonInKm(
        group.location.coordinates[0],
        group.location.coordinates[1],
        filterCoords[0],
        filterCoords[1]
      );
      return distance;
    } else if (group.location.coordinates) {
      const savedLocation = localStorage.getItem('location');
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation);
        if (parsedLocation) {
          const {longitude, latitude} = parsedLocation;
          distance = getDistanceFromLatLonInKm(
            group.location.coordinates[0],
            group.location.coordinates[1],
            longitude,
            latitude
          );
          return distance;
        }
      } else if (userInfos.location) {
        distance = getDistanceFromLatLonInKm(
          group.location.coordinates[0],
          group.location.coordinates[1],
          userInfos.location.lat,
          userInfos.location.lng
        );
      }
    }
  }, [group.location.coordinates, groupFilter.location, userInfos.location]);

  return (
    <Grid
      item
      xs={fullWidth ? 12 : 12}
      sm={fullWidth ? 12 : 6}
      md={fullWidth ? 12 : 6}
      lg={fullWidth ? 12 : 6}
    >
      <Tooltip title={t('open_group')} open={showMainTooltip}>
        <Card
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          sx={{
            width: '100%',
            cursor: 'pointer'
          }}
          onClick={() => {
            onGroupCardClick(group);
          }}
        >
          <CardMediaComponent
            height={cardMediaHeight}
            image={imageSource}
            altText={sportLabel}
            sport={sportLabel}
            showSport={true}
            distance={groupDistance}
            showOnlineStatus={true}
            sportNameSize={cardMediaTypography}
          />

          <CardContent
            sx={{
              maxHeight: smallerVariant ? 75 : 'none'
            }}
          >
            <Box display="flex" alignItems="center">
              <ResponsiveTypography
                {...responsiveTypographyTitleProps}
                truncateText
                alwaysTruncate
              >
                {group.title}
              </ResponsiveTypography>

              <Box display="flex" alignItems="center" marginLeft={1} gap={1}>
                {isUserMember && (
                  <Tooltip
                    title={t('member_long')}
                    placement="top"
                    onMouseEnter={handleOtherTooltipMouseEnter}
                    onMouseLeave={handleOtherTooltipMouseLeave}
                  >
                    <Done color="secondary" sx={{ml: 1}} />
                  </Tooltip>
                )}
                <Tooltip
                  title={t('participants_number')}
                  onMouseEnter={handleOtherTooltipMouseEnter}
                  onMouseLeave={handleOtherTooltipMouseLeave}
                >
                  <Chip
                    size={chipSize}
                    icon={<PersonIcon />}
                    label={group.members.length}
                    color="primary"
                  />
                </Tooltip>
                {group.accessType === 'PRIVATE' && (
                  <Tooltip
                    title={t('private_title')}
                    onMouseEnter={handleOtherTooltipMouseEnter}
                    onMouseLeave={handleOtherTooltipMouseLeave}
                  >
                    <Chip
                      size={chipSize}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <LockIcon sx={{position: 'relative', top: '1px'}} />
                        </Box>
                      }
                      color="primary"
                    />
                  </Tooltip>
                )}

                {unreadEventsCount > 0 && (
                  <Tooltip
                    title={t('unread_events_count')}
                    onMouseEnter={handleOtherTooltipMouseEnter}
                    onMouseLeave={handleOtherTooltipMouseLeave}
                  >
                    <Chip
                      size={chipSize}
                      icon={<NotificationsActiveIcon />}
                      label={unreadEventsCount}
                      color="primary"
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Tooltip>
    </Grid>
  );
};

export default GroupCardBig;
